import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, RefreshCw, Sparkles } from "lucide-react";

import {
  getVisualSemanticPlan,
  runVisualSemanticClustering,
} from "../../lib/brandStudioApi";

const GROUP_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  materiality: "Материалы / фактуры",
  photography: "Фотография",
  graphic_language: "Графический язык",
  composition: "Композиция",
  applications: "Носители",
};

export default function CompactSemanticUnderstandingOwnerPanelV1() {
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [error, setError] = useState("");

  const loadPlan = async () => {
    setLoadingPlan(true);
    setError("");
    try {
      setPlan(await getVisualSemanticPlan());
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail
          ? detail.code
          : typeof detail === "string"
            ? detail
            : "";
      setError(code || "Не удалось подготовить VU5.2 semantic plan.");
    } finally {
      setLoadingPlan(false);
    }
  };

  useEffect(() => {
    loadPlan();
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const pattern of result?.patterns || []) {
      if (!map[pattern.group]) map[pattern.group] = [];
      map[pattern.group].push(pattern);
    }
    return map;
  }, [result]);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const next = await runVisualSemanticClustering();
      setResult(next);
      await loadPlan();
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail
          ? detail.code
          : typeof detail === "string"
            ? detail
            : "";
      setError(code || "Не удалось выполнить compact semantic understanding.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="compact-semantic-understanding-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        OWNER QA · VU5.2
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Compact Semantic Understanding
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Сжимает VU5 evidence в короткие semantic patterns по отдельным группам.
        Raw atoms на этом экране не показываются.
      </p>

      {loadingPlan ? (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Проверяю cache и план вызовов…
        </div>
      ) : plan ? (
        <div className="mt-5 rounded-[14px] border border-[#DECDB7] bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#927A5B]">
            План перед запуском
          </div>
          <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
            Mistral-вызовов сейчас: <strong>{plan.provider_call_count}</strong>
            {" · "}cached groups: {plan.cached_group_count}
            {" · "}trivial groups: {plan.trivial_group_count}
          </div>
          <div className="mt-2 text-[11px] leading-5 text-[#796652]">
            В Mistral уйдут только группы со статусом needs_clustering. Cached и trivial
            повторно не отправляются.
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(plan.groups || []).map((item) => (
              <span
                key={item.group}
                className="rounded-full border border-[#E1D4C2] px-3 py-1.5 text-[10px] text-[#6D5842]"
              >
                {GROUP_LABELS[item.group] || item.group}: {item.status}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <button
        type="button"
        disabled={busy || loadingPlan || !plan}
        onClick={run}
        className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12px] text-[#FFF8ED] disabled:opacity-50"
        data-testid="compact-semantic-understanding-run-v1"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Собираю compact understanding…
          </>
        ) : plan?.provider_call_count === 0 ? (
          <>
            <RefreshCw size={14} />
            Собрать из cache
          </>
        ) : (
          <>
            <Sparkles size={14} />
            Запустить {plan?.provider_call_count || 0} group calls
          </>
        )}
      </button>

      {error && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} /> Compact understanding собран
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              calls: {result.provider_call_count} · cached: {result.cached_group_count}
            </span>
          </div>

          {(result.explicit_decisions || []).length > 0 && (
            <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Source Truth
              </div>
              <div className="mt-2 space-y-2">
                {result.explicit_decisions.map((item) => (
                  <div key={item.id} className="text-[11.5px] leading-5 text-[#4C392B]">
                    • {item.value}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(grouped).map(([group, patterns]) => (
              <article
                key={group}
                className="rounded-[14px] border border-[#DED0BC] bg-white p-4"
              >
                <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                  {GROUP_LABELS[group] || group}
                </div>
                <div className="mt-2 space-y-2">
                  {patterns.map((pattern) => (
                    <div key={pattern.id}>
                      <div className="text-[11.5px] leading-5 text-[#4C392B]">
                        • {pattern.label}
                      </div>
                      <div className="text-[9.5px] leading-4 text-[#8A745E]">
                        {pattern.strength} · refs: {pattern.reference_ids?.length || 0}
                      </div>
                    </div>
                  ))}
                  {!patterns.length && (
                    <div className="text-[11px] text-[#8A745E]">
                      Нет semantic patterns.
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-[#FFFCF6] p-4">
            <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
              Group health
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {(result.groups || []).map((item) => (
                <div key={item.group} className="text-[10.5px] leading-5 text-[#655141]">
                  {GROUP_LABELS[item.group] || item.group}: {item.mode}
                  {" · "}patterns {item.pattern_count}
                  {" · "}unclustered {item.unclustered_count}
                  {" · "}quarantined {item.quarantined_cluster_count}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 text-[10.5px] leading-5 text-[#8A745E]">
            Raw atoms hidden by contract. Unclustered evidence remains inside VU5 provenance
            and is not silently deleted.
          </div>
        </div>
      )}
    </section>
  );
}
